import { BreakpointObserver, BreakpointState, MediaMatcher } from '@angular/cdk/layout';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router, Event, NavigationEnd, NavigationStart } from '@angular/router';
import { BREAKPOINTS_MEDIUM, PORTAL_RELEASE_VERSION } from '@app-core/constants/constants';
import { DataService } from '@app-core/services/data/data.service';
import { LiveTelematicsService } from '@app-core/services/live-telematics/live-telematics.service';
import { getSideNavigationConfigState } from '@app-shared/reducers';
import { State } from '@app-shared/reducers/sidenavigation-config.reducer';
import { CLIENT_CONFIG, navigationRoutes } from '@config/config';
import { Store } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { SideNavRouterLink } from '@app-core/models/core.model';
import { TranslateService } from '@ngx-translate/core';
import { MixPanelService } from '@app-core/services/mixpanel/mix-panel.service';
import { environment } from '@env/environment';
import { UnsavedConfigurationsModalComponent } from '../unsaved-confiurations-modal/unsaved-confiurations-modal.component';
import { CoachingConfigTabComponent } from '@app-asset-config/pages/coaching-config-tab/coaching-config-tab.component';

@Component({
  selector: 'app-side-navigation',
  templateUrl: './side-navigation.component.html',
  styleUrls: ['./side-navigation.component.scss'],
})
export class SideNavigationComponent implements OnInit, OnDestroy {
  @ViewChild('sidenav', { static: true }) public sideNav: SideNavigationComponent;
  @ViewChild('coachingConfig')
  coachingConfigComponent: CoachingConfigTabComponent;

  public readonly isGeotabEnv = Boolean(environment.isGeotabEnv);
  public mobileQuery: MediaQueryList;
  public navigationRoutes = [];
  public isSideNavOpen = false;
  public config = CLIENT_CONFIG;
  private ngUnsubscribe = new Subject();
  public currentTheme = 'light';
  public isFaqOpened = false;
  public isBulkUpdateOpened = false;
  public isFaqConfigOpened: boolean = false;
  public isModifyConfigOpened: boolean = false;
  public isCoachingHelpOpened: boolean = false;
  public configurationId: string;
  public currentLanguage: string;
  public featureId: string;
  public portalReleaseVersion = PORTAL_RELEASE_VERSION;
  public clientConfig = CLIENT_CONFIG;
  public backgroundColor: string;
  public color: string;
  public isTouchDevice = false;
  private bulkUpdateSubscription: Subscription;
  constructor(
    private store: Store<State>,
    private changeDetectorRef: ChangeDetectorRef,
    private media: MediaMatcher,
    private dataService: DataService,
    private router: Router,
    private liveTelematicsService: LiveTelematicsService,
    private dialog: MatDialog,
    private breakpointObserver: BreakpointObserver,
    private translate: TranslateService,
    private mixpanelService: MixPanelService
  ) {
    this.mobileQuery = this.media.matchMedia('(max-width: 1440px)');
    this.mobileQuery.addListener(this.mobileQueryListener);
  }

  public ngOnInit() {
    if (window.self != window.top) {
      window.addEventListener('message', this.handleToggleSideNavEvent.bind(this));
    }

    this.subscribeForLiveTelematicsConfigChange();
    this.subscribeForSideNavConfigChanges();

    this.breakpointObserver
      .observe(BREAKPOINTS_MEDIUM)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((state: BreakpointState) => {
        this.isTouchDevice = state.matches;
      });

    this.router.events.pipe(takeUntil(this.ngUnsubscribe)).subscribe((event: Event) => {
      if (event instanceof NavigationStart || event instanceof NavigationEnd) {
        this.closeFaq();
        this.closeConfigFaq();
        this.closeModifyConfig();
        this.closeBulkUpdate();
        this.dialog.closeAll();
      }
    });

    this.dataService._currentTheme.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      if (value) {
        this.currentTheme = value;
        if (value === 'light' && this.clientConfig?.sideNavColor) {
          this.backgroundColor = this.clientConfig?.sideNavColor?.lightBackgroundColor;
          this.color = this.clientConfig?.sideNavColor?.lightColor;
        } else {
          this.backgroundColor = this.clientConfig?.sideNavColor?.darkBackgroundColor;
          this.color = this.clientConfig?.sideNavColor?.darkColor;
        }
      }
    });

    this.dataService._currentFeatureFaq.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      if (value) {
        this.featureId = value;
        this.isFaqOpened = true;
      }
    });

    this.bulkUpdateSubscription = this.dataService._currentBulkUpdate
      .pipe(distinctUntilChanged(), takeUntil(this.ngUnsubscribe)) // Prevent repeated values
      .subscribe((value: string) => {
        this.isBulkUpdateOpened = value === 'true'; // Ensure only 'true' opens it
      });

    this.dataService._currentConfigFaq.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      if (value) {
        this.configurationId = value;
        this.isFaqConfigOpened = true;
      }
    });

    this.dataService._currentModifyConfig.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      if (value) {
        this.isModifyConfigOpened = true;
      }
    });

    this.dataService._currentCoachingHelp.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      if (value) {
        this.isCoachingHelpOpened = true;
      }
    });

    this.dataService._currentLanguage.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      if (value) {
        this.currentLanguage = value;
        this.translate.use(value);
      }
    });
  }
  openBulkUpdate() {
    this.dataService.openBulkUpdate('true'); // Explicitly trigger it
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.mobileQuery.removeListener(this.mobileQueryListener);
    window.removeEventListener('message', this.handleToggleSideNavEvent.bind(this));

    if (this.bulkUpdateSubscription) {
      this.bulkUpdateSubscription.unsubscribe(); // Prevent memory leaks
    }
  }

  private subscribeForLiveTelematicsConfigChange() {
    this.liveTelematicsService.isLiveTelematicsEnabled.pipe(takeUntil(this.ngUnsubscribe)).subscribe((liveTelematicsEnabled) => {
      this.updateNavigationRoutes(liveTelematicsEnabled);
    });
  }

  private updateNavigationRoutes(LiveViewEnabled?: boolean) {
    if (LiveViewEnabled) {
      this.navigationRoutes = [...navigationRoutes];
    } else {
      this.navigationRoutes = navigationRoutes.filter((r) => r.routerLink !== '/live-view');
      if (this.router.url.split('?')[0] === '/live-view') {
        this.router.navigate(['home']);
      }
    }
  }

  public subscribeForSideNavConfigChanges() {
    this.store
      .select(getSideNavigationConfigState)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((sideNavigationConfigState) => {
        this.isSideNavOpen = sideNavigationConfigState.isSideNavOpen;
        const width = this.isSideNavOpen ? '220px' : '0px';

        document.documentElement.style.setProperty('--main-sidebar-width', width);
      });
  }

  private mobileQueryListener() {
    if (this.changeDetectorRef) {
      this.changeDetectorRef.detectChanges();
    }
  }

  public closeFaq() {
    this.isFaqOpened = false;
    this.featureId = null;
    this.dataService.currentFeatureFaq = null;
  }

  public closeConfigFaq() {
    this.isFaqConfigOpened = false;
    this.configurationId = null;
    this.dataService.currentConfigFaq = null;
  }

  public closeModifyConfig() {
    if (this.coachingConfigComponent?.hasUnsavedChanges) {
      const dialogRef = this.dialog.open(UnsavedConfigurationsModalComponent, {
        panelClass: ['coaching-confirmation-modal', 'mobile-modal'],
        autoFocus: false,
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result === 'discard') {
          this.isModifyConfigOpened = false;
          this.dataService.currentModifyConfig = null;
        }
        if (result === 'stay') {
          return;
        }
      });
    } else {
      this.isModifyConfigOpened = false;
      this.dataService.currentModifyConfig = null;
    }
  }

  public closeCoachingHelp() {
    this.isCoachingHelpOpened = false;
    this.dataService.currentCoachingHelp = null;
  }

  public closeBulkUpdate() {
    this.isBulkUpdateOpened = false;
    this.dataService.openBulkUpdate('false'); // Reset the value
  }

  public sideNavPageList(route: SideNavRouterLink) {
    const pageName = route.label;
    const pageMappings: { [key: string]: string } = {
      Home: 'Home Page Clicked',
      'Safety Events': 'Safety Events Page Clicked',
      Trips: 'Trips Page Clicked',
      'Live View': 'Live View Page Clicked',
      Coaching: 'Coaching Page Clicked',
      'Video Requests': 'Video Requests Page Clicked',
      Drivers: 'Drivers Page Clicked',
      Challenges: 'Challenges Page Clicked',
      Reports: 'Reports Page Clicked',
      Assets: 'Assets Page Clicked',
      Users: 'Users Page Clicked',
      Configurations: 'Configurations Page Clicked',
    };
    const displayPageName = pageMappings[pageName] || 'Downloads Page Clicked';
    this.mixpanelService.track(displayPageName, {
      'Page Name': pageName,
    });
  }
  private handleToggleSideNavEvent(event: MessageEvent) {
    if (event?.data && event?.data?.eventType === 'toggleSidenav') {
      this.isSideNavOpen = event?.data?.show;
    }
  }
}
